<template>
  <div class="about-us-page-container">
    <!-- 页面级背景容器 -->
    <div class="page-content-wrapper">
      <!-- 内容约束和可能的内层背景/叠加层 -->

      <section class="who-we-are-section content-section" id="who-we-are">
        <h2 class="main-title">{{ $t("aboutPage.whoWeAre.title") }}</h2>
        <p class="sub-title">{{ $t("aboutPage.whoWeAre.subTitle") }}</p>
        <div class="content-wrapper">
          <p class="paragraph">{{ $t("aboutPage.whoWeAre.p1") }}</p>
          <p class="paragraph">{{ $t("aboutPage.whoWeAre.p2") }}</p>
        </div>
      </section>

      <section class="our-team-section content-section" id="our-team">
        <h3 class="section-header">{{ $t("aboutPage.ourTeam.title") }}</h3>
        <div class="team-gallery">
          <img
            v-for="(image, index) in teamImages"
            :key="index"
            :src="image.src"
            :alt="image.alt"
            class="team-image"
          />
        </div>
      </section>

      <section class="our-strengths-section content-section" id="our-strengths">
        <h3 class="section-header">
          {{ $t("aboutPage.ourStrengths.title") || "Our Strengths" }}
        </h3>
        <div class="grid-container">
          <div
            class="strength-item"
            v-for="strength in strengths"
            :key="strength.id"
          >
            <div class="strength-image-wrapper">
              <img
                :src="strength.image"
                :alt="strength.title"
                class="strength-image"
              />
            </div>
            <div class="strength-text-content">
              <span class="strength-number">{{ strength.id }}</span>
              <h4 class="strength-title">{{ strength.title }}</h4>
              <p class="strength-description">{{ strength.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <stats-section id="about-page-stats" class="content-section" />
    </div>
  </div>
</template>

<script>
import StatsSection from "../components/StatsSection.vue";

export default {
  name: "AboutUsPage",
  components: {
    StatsSection,
  },
  computed: {
    teamImages() {
      const imageKeys = ["team1", "team2", "team3", "team4", "team5"];
      return imageKeys.map((key) => {
        let imageSrc = "";
        try {
          imageSrc = require(`@/assets/about-images/${key}.jpg`);
        } catch (e) {
          console.warn(
            `团队图片未找到: src/assets/about-images/${key}.jpg. 使用占位符。`
          );
          imageSrc = `https://via.placeholder.com/200x150.png?text=${key}`;
        }
        return {
          src: imageSrc,
          alt:
            this.$t(`aboutPage.ourTeam.imageAlt.${key}`) ||
            `Team Member ${key.replace("team", "")}`,
        };
      });
    },
    strengths() {
      const i18nStrengths = this.$t("aboutPage.ourStrengths.items");
      if (Array.isArray(i18nStrengths)) {
        return i18nStrengths.map((item) => {
          let imageSrc = "";
          try {
            imageSrc = require(`@/assets/about-images/strength-${item.id}.png`);
          } catch (e) {
            console.warn(
              `优势图片未找到: src/assets/about-images/strength-${item.id}.png. 使用占位符。`
            );
            imageSrc = `https://via.placeholder.com/180x120.png?text=Strength ${item.id}`;
          }
          return {
            id: item.id,
            title: item.title,
            description: item.description,
            image: imageSrc,
          };
        });
      }
      return [];
    },
  },
};
</script>

<style scoped>
.about-us-page-container {
  /* 页面级背景图片 */
  background-image: url("~@/assets/major-media-styled/bj.jpg");
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  min-height: 100vh;
  padding-top: 60px; /* 给页面顶部一些空间 */
  padding-bottom: 60px; /* 给页面底部一些空间 */
  box-sizing: border-box;
  overflow-x: hidden; /* 防止水平滚动条 */
}

/* 新增：用于包裹所有内容的内部容器，可以设置最大宽度和居中 */
.page-content-wrapper {
  max-width: 1100px; /* 内容最大宽度 */
  margin: 0 auto;
  padding: 0 20px; /* 容器左右内边距 */
}

/* 通用区块样式 */
.content-section {
  background-color: rgba(255, 255, 255, 0.92); /* 区块的半透明白色背景 */
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  margin-bottom: 50px; /* 区块之间的统一间距 */
  position: relative;
  z-index: 1;
}
.content-section:last-child {
  margin-bottom: 0; /* 最后一个区块不需要下边距 */
}

/* 标题样式 */
.main-title,
.section-header {
  text-align: center;
  color: #1a2b4d; /* 深蓝黑色标题 */
  font-family: "Acumin Variable Concept", "Inter", sans-serif; /* 假设已加载 */
  font-weight: 700;
  margin-bottom: 15px;
}
.main-title {
  font-size: 2.8em; /* 主标题稍大 */
  margin-bottom: 10px; /* 主标题和副标题间距 */
}
.sub-title {
  font-size: 1.1em;
  text-align: center;
  color: #5a677d;
  margin-bottom: 40px;
  text-transform: uppercase;
  font-weight: 500;
}
.section-header {
  /* 用于 "Our Team" 和 "Our Strengths" 的标题 */
  font-size: 2em;
  padding-bottom: 10px;
  border-bottom: 3px solid #4a90e2; /* 蓝色下划线 */
  display: inline-block; /* 使下划线只作用于文字宽度 */
  margin-bottom: 30px;
  /* 为了让 display:inline-block 的元素居中 */
  display: table;
  margin-left: auto;
  margin-right: auto;
}

/* Who We Are Section */
.who-we-are-section .content-wrapper {
  /* content-wrapper 已被 .content-section 替代大部分样式 */
  max-width: 800px; /* 可以限制此特定区块的文本宽度 */
  margin: 0 auto;
  font-size: 1.05em;
  line-height: 1.8;
  color: #2d3748;
}
.who-we-are-section .paragraph {
  margin-bottom: 1.5em;
  text-align: left; /* 或者 justify，但 left 通常更易读 */
}

/* Our Team Section */
.team-gallery {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(180px, 1fr)
  ); /* auto-fill 更好 */
  gap: 20px;
}
.team-image {
  width: 100%;
  border-radius: 20px;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  object-position: center;
  display: block;
  background-color: #eef2f7;
  /* box-shadow: 0 5px 15px rgba(0,0,0,0.1); */
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.team-image:hover {
  transform: scale(1.05);
  /* box-shadow: 0 8px 20px rgba(0,0,0,0.15); */
}

/* Our Strengths Section */
/* .our-strengths-section 的背景等由 .content-section 控制 */
.our-strengths-section .grid-container {
  /* 新增一个内部grid容器 */
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); /* 调整minmax */
  gap: 30px;
}

.strength-item {
  display: flex;
  align-items: flex-start;
  gap: 25px; /* 图文间距 */
  background-color: #ffffff; /* 保持白色背景 */
  padding: 30px; /* 增大内边距 */
  border-radius: 10px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.07);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.strength-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.strength-image-wrapper {
  flex-shrink: 0;
  width: 120px; /* 给图片包裹一个固定宽度 */
  height: 120px;
  border-radius: 8px;
  overflow: hidden; /* 确保图片在圆角内 */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f4f8; /* 图片区域浅背景 */
}
.strength-image {
  width: 100%; /* 图片适应包裹层 */
  height: 100%;
  object-fit: contain; /* 或 cover，取决于图片内容 */
}

.strength-text-content {
  flex-grow: 1;
  padding-top: 5px; /* 轻微调整文本垂直对齐 */
}
.strength-number {
  font-size: 2.2em;
  font-weight: 700; /* 更粗的数字 */
  color: #3498db;
  display: block;
  margin-bottom: 5px;
  line-height: 1;
}
.strength-title {
  font-size: 1.35em;
  color: #2c3e50;
  font-weight: 600; /* 标题加粗 */
  margin-bottom: 10px;
}
.strength-description {
  font-size: 0.95em;
  line-height: 1.7;
  color: #4a5568;
}

/* StatsSection (假设它有自己的内边距和样式来适应) */

@media (max-width: 992px) {
  .our-strengths-section .grid-container {
    grid-template-columns: 1fr;
  }
  .page-content-wrapper {
    padding: 0 15px;
  }
}
@media (max-width: 768px) {
  .about-us-page-container {
    padding-top: 40px;
    padding-bottom: 40px;
  }
  .content-section {
    padding: 30px;
    margin-bottom: 40px;
  }
  .main-title {
    font-size: 2em;
  }
  .section-header {
    font-size: 1.6em;
    margin-bottom: 25px;
  }
  .sub-title {
    font-size: 1em;
    margin-bottom: 30px;
  }

  .who-we-are-section .content-wrapper {
    padding: 20px;
    font-size: 1em;
  }
  .team-gallery {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 15px;
  }
  .strength-item {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 25px;
  }
  .strength-image-wrapper {
    margin-bottom: 20px;
    width: 100px;
    height: 100px;
  }
  .our-strengths-section .grid-container {
    gap: 20px;
  }
}
</style>
